// src/context/GlobalFiltersContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import {
    formatTimeRange,
    getTimeRangeOptions,
    handleTimeRangeChange as handleTimeRangeUtil
} from '../utils/timeRangeUtils';
import {
    getOrganizationCurrencySettings,
    updateOrganizationDisplayCurrency
} from '../services/api/organization-currency';
import { getUntaggedTransactionCount } from '../services/api/transactions';
import { useToast } from '../components/ui/Toast';

const GlobalFiltersContext = createContext();

export const useGlobalFilters = () => useContext(GlobalFiltersContext);

export const GlobalFiltersProvider = ({ children }) => {
    const toast = useToast();
    const { currentOrganization } = useAuth();

    // Currency state
    const [displayCurrency, setDisplayCurrency] = useState('USD');
    const [supportedCurrencies, setSupportedCurrencies] = useState([]);
    const [isLoadingCurrency, setIsLoadingCurrency] = useState(false);

    // Time range state
    const [timeRangePreset, setTimeRangePreset] = useState('this_month');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showCustomRange, setShowCustomRange] = useState(false);

    // Untagged transactions state
    const [untaggedTransactionCount, setUntaggedTransactionCount] = useState(null);

    const timeRangeOptions = useMemo(() => getTimeRangeOptions(), []);

    const apiTimeRange = useMemo(() => {
        return formatTimeRange(timeRangePreset, startDate, endDate, showCustomRange);
    }, [timeRangePreset, startDate, endDate, showCustomRange]);

    const simpleTimeRange = useMemo(() => {
        return showCustomRange ? 'custom' : timeRangePreset;
    }, [timeRangePreset, showCustomRange]);

    const fetchCurrencySettings = useCallback(async () => {
        const orgId = localStorage.getItem('selectedOrganizationId');
        if (!orgId) return;

        setIsLoadingCurrency(true);
        try {
            const settings = await getOrganizationCurrencySettings(orgId);
            setDisplayCurrency(settings.display_currency);
            setSupportedCurrencies(settings.supported_currencies || []);
        } catch (error) {
            console.error('Error fetching currency settings:', error);
        } finally {
            setIsLoadingCurrency(false);
        }
    }, []);

    const fetchUntaggedCount = useCallback(async () => {
        try {
            const count = await getUntaggedTransactionCount();
            setUntaggedTransactionCount(count);
        } catch (error) {
            console.error('Error fetching untagged count:', error);
        }
    }, []);

    const handleCurrencyChange = useCallback(async (newCurrency) => {
        try {
            const orgId = localStorage.getItem('selectedOrganizationId');
            await updateOrganizationDisplayCurrency(orgId, newCurrency);
            setDisplayCurrency(newCurrency);
            toast.success(`Currency changed to ${newCurrency}`);
        } catch (error) {
            console.error('Error updating currency:', error);
            toast.error(error.response?.data?.detail || error.message || 'Failed to update currency');
        }
    }, [toast]);

    const handleTimeRangeChange = useCallback((e) => {
        const selectedValue = e.target.value;
        handleTimeRangeUtil(selectedValue, setTimeRangePreset, setShowCustomRange);
    }, []);

    const handleStartDateChange = useCallback((e) => setStartDate(e.target.value), []);
    const handleEndDateChange = useCallback((e) => setEndDate(e.target.value), []);

    // Fetch data when organization changes
    useEffect(() => {
        if (currentOrganization) {
            fetchCurrencySettings();
            fetchUntaggedCount();
        }
    }, [currentOrganization, fetchCurrencySettings, fetchUntaggedCount]);

    const value = {
        // Currency
        displayCurrency,
        supportedCurrencies,
        isLoadingCurrency,
        handleCurrencyChange,
        fetchCurrencySettings,

        // Time range
        timeRangePreset,
        startDate,
        endDate,
        showCustomRange,
        timeRangeOptions,
        apiTimeRange,
        simpleTimeRange,
        handleTimeRangeChange,
        handleStartDateChange,
        handleEndDateChange,
        setStartDate,
        setEndDate,

        // Untagged transactions
        untaggedTransactionCount,
        fetchUntaggedCount,
    };

    return (
        <GlobalFiltersContext.Provider value={value}>
            {children}
        </GlobalFiltersContext.Provider>
    );
};
